import React, { PropsWithChildren, useRef, useLayoutEffect } from "react"
import {
  Box,
  Drawer,
  DrawerProps,
  Stack,
  Typography,
  TypographyProps
} from "@mui/material"
import { unstable_useId as useId } from "@mui/material/utils"
import { FaIcon } from "../../../../mp-core/icons"
import IconButton from "../IconButton"
import { DrawerSize, useStyles } from "./styles"
import { formatDataAutomationId } from "@masteryprep/qti-components"

type Props = {
  open: boolean
  onClose: () => void
  onExited?: () => void
  title?: React.ReactNode
  titleProps?: Partial<TypographyProps> & {
    component?: React.ElementType
  }
  actions?: React.ReactNode
  titleAction?: React.ReactNode
  size?: DrawerSize
  paperClassName?: string
  anchor?: DrawerProps["anchor"]
  backdropVisible?: boolean
  className?: string
  backdropProps?: Partial<React.HTMLAttributes<HTMLDivElement>> & {
    "data-automation-id"?: string
  }
  contentType?: string // Added to support different section types
  zIndex?: number
}

// a11y: inert is ref-counted per element so overlapping drawers closing out of
// order don't un-inert the background early (same idea as FullscreenModal's
// open-modal counter). Elements inert for another owner (e.g. a parent modal's
// background) are never tracked here, so they're left alone.
const inertCounts = new Map<Element, number>()

const addInert = (el: Element) => {
  inertCounts.set(el, (inertCounts.get(el) ?? 0) + 1)
  el.setAttribute("inert", "")
}

const removeInert = (el: Element) => {
  const count = inertCounts.get(el)
  if (!count) return
  if (count === 1) {
    inertCounts.delete(el)
    el.removeAttribute("inert")
  } else {
    inertCounts.set(el, count - 1)
  }
}

const CustomDrawer: React.FC<PropsWithChildren<Props>> = ({
  open,
  title,
  titleProps,
  children,
  actions,
  onClose,
  onExited,
  titleAction,
  size = "md",
  paperClassName,
  anchor = "right",
  backdropVisible = false,
  className,
  backdropProps = {},
  contentType,
  zIndex
}) => {
  const { classes, cx } = useStyles({ size })
  const rootRef = useRef<HTMLDivElement | null>(null)
  const inertedRef = useRef<Element[]>([])
  const triggerRef = useRef<HTMLElement | null>(null)
  const pendingRestoreRef = useRef<number | null>(null)
  const titleId = useId()

  // a11y: disableEnforceFocus + disableRestoreFocus (below) let AT virtual
  // cursors (e.g. ChromeVox) read the drawer body; we trap the keyboard by
  // inerting everything behind the drawer instead (nodes already inert, e.g. a
  // parent modal's background, are left for their owner), and restore focus to
  // the opener ourselves on close. The restore is deferred one frame because
  // keepMounted leaves the drawer content focusable, so React's post-commit
  // restoreSelection re-focuses it and would clobber a synchronous restore.
  useLayoutEffect(() => {
    if (!open) return undefined
    // a11y: cancel a pending focus-restore from a previous close so a rapid
    // close -> reopen doesn't pull focus behind the freshly opened drawer.
    if (pendingRestoreRef.current !== null) {
      cancelAnimationFrame(pendingRestoreRef.current)
      pendingRestoreRef.current = null
    }
    const root = rootRef.current
    // a11y: if a sibling inerted our own root while we were closed (keepMounted),
    // strip it so we don't open inert.
    if (root?.hasAttribute("inert")) {
      inertCounts.delete(root)
      root.removeAttribute("inert")
    }
    triggerRef.current = document.activeElement as HTMLElement | null
    const inerted = root
      ? Array.from(document.body.children).filter(
          el =>
            el !== root &&
            !el.contains(root) &&
            (inertCounts.has(el) || !el.hasAttribute("inert"))
        )
      : []
    inerted.forEach(addInert)
    inertedRef.current = inerted
    return () => {
      inertedRef.current.forEach(removeInert)
      inertedRef.current = []
      const trigger = triggerRef.current
      triggerRef.current = null
      if (!trigger || !document.contains(trigger)) return
      pendingRestoreRef.current = requestAnimationFrame(() => {
        pendingRestoreRef.current = null
        if (document.contains(trigger)) trigger.focus()
      })
    }
  }, [open])

  return (
    <Drawer
      ref={rootRef}
      open={open}
      onClose={onClose}
      anchor={anchor}
      className={className}
      sx={theme => ({
        zIndex: zIndex || theme.zIndex.modal + 1
      })}
      componentsProps={{
        backdrop: {
          className: cx(classes.backdrop, {
            [classes.backdropVisible]: backdropVisible
          }),
          ...backdropProps
        }
      }}
      PaperProps={{
        className: cx(classes.drawer, paperClassName),
        // a11y: MUI Drawer doesn't name its paper as a dialog; give it a dialog
        // role + accessible name so it announces as "{title} dialog".
        ...(title
          ? { role: "dialog", "aria-modal": true, "aria-labelledby": titleId }
          : {})
      }}
      SlideProps={{ onExited }}
      ModalProps={{
        disableScrollLock: true,
        hideBackdrop: false,
        keepMounted: true,
        disableEnforceFocus: true,
        disableRestoreFocus: true
      }}
    >
      <Stack
        className={classes.container}
        data-automation-id={formatDataAutomationId(`${contentType}-drawer`)}
      >
        {title && (
          <Stack py={3} px={4} className={classes.title} gap={3}>
            <IconButton
              size="xsmall"
              onClick={onClose}
              aria-label={
                title && typeof title === "string" ? `Close ${title}` : "Close"
              }
              title={
                title && typeof title === "string" ? `Close ${title}` : "Close"
              }
            >
              <FaIcon solid name="times" />
            </IconButton>
            <Typography variant="h4" {...titleProps} id={titleId}>
              {title}
            </Typography>
            {titleAction && <Box ml="auto">{titleAction}</Box>}
          </Stack>
        )}
        {children}
        {actions && (
          <Stack p={4} gap={2} className={classes.actions}>
            {actions}
          </Stack>
        )}
      </Stack>
    </Drawer>
  )
}

export default CustomDrawer
